/**
 * lib/payrollPeriod.ts
 * ------------------------------------------------------------------
 * HR PAYROLL — month close / archive status model (pure).
 *
 * A payroll month is OPEN (editable) while it is the current or a
 * future month, and CLOSED (locked, immutable) once it has elapsed.
 * The close is therefore AUTOMATIC and date-derived — no scheduled
 * job writes anything. The only stored state is an explicit OVERRIDE
 * document per period, used for:
 *   - an admin REOPENING an elapsed month (audited), and
 *   - explicitly CLOSING the current month early.
 *
 * Effective status (most-specific wins):
 *   override 'reopened'  → OPEN   (admin override beats the calendar)
 *   override 'closed'    → CLOSED (explicit early close)
 *   month elapsed        → CLOSED (auto, the default)
 *   otherwise            → OPEN   (current / future)
 *
 * Firestore rules mirror exactly this rule on hrPayslips writes so a
 * closed month cannot be mutated client-side (see the rules block in
 * the implementation doc). Keep this file framework-free.
 * ------------------------------------------------------------------ */

/** Firestore collection of per-period override docs (id = `${year}_${month}`). */
export const PAYROLL_PERIOD_STATUS_COLLECTION = 'hrPayrollPeriodStatus';

/**
 * Days after month-end before an un-overridden month auto-closes.
 * 0 = lock exactly at month-end (the confirmed decision: "last day of
 * the month"). Raise to e.g. 5 to leave a window for finalising the
 * just-ended month in arrears without needing an admin reopen.
 * Must match the equivalent grace in the Firestore rules.
 */
export const CLOSE_GRACE_DAYS = 0;

export type PeriodOverride = 'closed' | 'reopened';

export interface PayrollPeriodStatusDoc {
  id?: string;
  year: number;
  month: number; // 1–12
  /** Absence = no override (status is date-derived). */
  override?: PeriodOverride;
  closedAt?: string;
  closedBy?: string;
  reopenedAt?: string;
  reopenedBy?: string;
  reopenReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function periodKey(year: number, month: number): string {
  return `${year}_${month}`;
}

export function monthIndex(year: number, month: number): number {
  return year * 12 + (month - 1);
}

/** True once the period (year, month) has fully elapsed past the grace window. */
export function monthHasElapsed(year: number, month: number, now: Date = new Date()): boolean {
  // First day of the month AFTER the period (JS month is 0-based, so
  // `month` already points at the next calendar month), plus the grace.
  const cutoff = new Date(year, month, 1);
  cutoff.setDate(cutoff.getDate() + CLOSE_GRACE_DAYS);
  return now.getTime() >= cutoff.getTime();
}

export type EffectiveStatus = 'open' | 'closed';
export type LockReason = 'future' | 'current' | 'auto-closed' | 'closed' | 'reopened';

export interface PeriodLockState {
  year: number;
  month: number;
  status: EffectiveStatus;
  locked: boolean;
  reason: LockReason;
  /** True when the status is date-derived rather than an explicit override. */
  auto: boolean;
}

export function effectivePeriodStatus(
  year: number,
  month: number,
  doc?: PayrollPeriodStatusDoc,
  now: Date = new Date(),
): PeriodLockState {
  if (doc?.override === 'reopened') {
    return { year, month, status: 'open', locked: false, reason: 'reopened', auto: false };
  }
  if (doc?.override === 'closed') {
    return { year, month, status: 'closed', locked: true, reason: 'closed', auto: false };
  }
  if (monthHasElapsed(year, month, now)) {
    return { year, month, status: 'closed', locked: true, reason: 'auto-closed', auto: true };
  }
  const cur = monthIndex(now.getFullYear(), now.getMonth() + 1);
  const reason: LockReason = monthIndex(year, month) > cur ? 'future' : 'current';
  return { year, month, status: 'open', locked: false, reason, auto: true };
}

/** Short human badge label for an effective state. */
export function lockLabel(state: PeriodLockState): string {
  switch (state.reason) {
    case 'reopened': return 'Reopened';
    case 'closed': return 'Closed';
    case 'auto-closed': return 'Closed';
    case 'future': return 'Future';
    case 'current': return 'Open';
  }
}

/** Longer explanation for tooltips / banners. */
export function lockDescription(state: PeriodLockState): string {
  switch (state.reason) {
    case 'reopened': return 'Reopened by an admin — editable until re-closed.';
    case 'closed': return 'Closed early by an admin — locked.';
    case 'auto-closed': return 'Automatically closed at month-end — locked.';
    case 'future': return 'Future month — not yet payable.';
    case 'current': return 'Current month — open for the pay run.';
  }
}
